import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Card, Col, Row, Statistic, Typography, Empty } from "antd";
import {
  TeamOutlined,
  ShoppingCartOutlined,
  FormOutlined,
  InboxOutlined,
  DeliveredProcedureOutlined,
  PieChartOutlined,
  DollarOutlined,
  UserOutlined,
  LineChartOutlined,
} from "@ant-design/icons";
import { USER_INFO } from "../../utils/settings/config";
import { donHangService } from "../../services/donHangService";
import { donShipService } from "../../services/donShipService";
import { orderService } from "../../services/orderService";
import { nhanDonService } from "../../services/nhanDonService";
import { ketThucService } from "../../services/ketThucService";
import { chiPhiService } from "../../services/chiPhiService";
import { khachHangService } from "../../services/khachHangService";

const { Title, Text } = Typography;

const getUserInfo = () => {
  const raw = localStorage.getItem(USER_INFO);
  return raw ? JSON.parse(raw) : null;
};

// Gộp permission của mọi role mà user đang có - cùng logic với menu ở HomeTemplate,
// để dashboard chỉ hiện đúng những mảng mà role của user được đọc (read).
const combinePermissions = (roles) => {
  const combined = {};
  (roles || []).forEach((role) => {
    for (const resource in role) {
      if (!combined[resource]) {
        combined[resource] = { ...role[resource] };
      } else {
        for (const action in role[resource]) {
          combined[resource][action] =
            combined[resource][action] || role[resource][action];
        }
      }
    }
  });
  return combined;
};

// Các trạng thái đơn hàng cần theo dõi nhanh ngay trên dashboard
const TRANG_THAI_DON_HANG_CAN_THEO_DOI = [
  { id: 1, ten: "Mới lên đơn" },
  { id: 5, ten: "Hàng đã về, đợi ship" },
  { id: 9, ten: "Nguồn hàng ship thiếu" },
];

// Danh sách khối chức năng trên dashboard, ứng với từng resource quyền hạn.
// key: tên field trong state tongQuan lưu số liệu tổng quan (totalCount) của khối đó.
const RESOURCE_CARDS = [
  {
    resource: "don_hang",
    label: "Chốt Đơn",
    icon: <ShoppingCartOutlined />,
    path: "/don-hang",
    color: "#1890ff",
  },
  {
    resource: "order",
    label: "Mua Hàng",
    icon: <FormOutlined />,
    path: "/order",
    color: "#722ed1",
    key: "order",
  },
  {
    resource: "nhan_don",
    label: "Soạn Hàng",
    icon: <InboxOutlined />,
    path: "/nhan-don",
    color: "#fa8c16",
    key: "nhan_don",
  },
  {
    resource: "giao_hang",
    label: "Giao Hàng",
    icon: <DeliveredProcedureOutlined />,
    path: "/giao-hang",
    color: "#13c2c2",
    key: "giao_hang",
  },
  {
    resource: "ket_thuc",
    label: "Kết Thúc",
    icon: <PieChartOutlined />,
    path: "/ket-thuc",
    color: "#52c41a",
    key: "ket_thuc",
  },
  {
    resource: "chi_phi",
    label: "Chi Phí",
    icon: <DollarOutlined />,
    path: "/chi-phi",
    color: "#f5222d",
    key: "chi_phi",
  },
  {
    resource: "khach_hang",
    label: "Khách Hàng",
    icon: <TeamOutlined />,
    path: "/khach-hang",
    color: "#eb2f96",
    key: "khach_hang",
  },
  {
    resource: "thong_ke_bao_cao",
    label: "Thống Kê Báo Cáo",
    icon: <LineChartOutlined />,
    path: "/thong-ke-bao-cao/thong-ke-doanh-thu-chi-phi",
    color: "#2f54eb",
  },
  {
    resource: "quan_tri",
    label: "Quản Trị",
    icon: <UserOutlined />,
    path: "/quan-tri/quan-ly-nguoi-dung",
    color: "#8c8c8c",
  },
];

export default function Home() {
  const history = useHistory();
  const [userInfo] = useState(getUserInfo());
  const [loading, setLoading] = useState(true);
  const [donHangTheoTrangThai, setDonHangTheoTrangThai] = useState([]);
  const [tongQuan, setTongQuan] = useState({});

  const permissions = combinePermissions(userInfo?.permission);
  const coQuyenDoc = (resource) => !!permissions?.[resource]?.read;

  useEffect(() => {
    let daHuy = false;
    const tasks = [];

    const layTongCount = async (key, fetcher) => {
      try {
        const soLuong = await fetcher();
        if (!daHuy) setTongQuan((prev) => ({ ...prev, [key]: soLuong }));
      } catch (error) {
        if (!daHuy) setTongQuan((prev) => ({ ...prev, [key]: null }));
      }
    };

    if (coQuyenDoc("don_hang")) {
      tasks.push(
        Promise.all(
          TRANG_THAI_DON_HANG_CAN_THEO_DOI.map(async (tt) => {
            try {
              const { data } = await donHangService.layDanhSachDonHangPhanTrangAPI(
                1,
                1,
                "",
                "",
                tt.id,
                "",
                ""
              );
              return { ...tt, soLuong: data?.data?.totalCount ?? 0 };
            } catch (error) {
              return { ...tt, soLuong: null };
            }
          })
        ).then((ketQua) => {
          if (!daHuy) setDonHangTheoTrangThai(ketQua);
        })
      );
    }

    if (coQuyenDoc("order")) {
      tasks.push(
        layTongCount("order", async () => {
          const { data } = await orderService.layDanhSachOrderKeywordPhanTrangAPI(1, 1);
          return data?.data?.totalCount ?? 0;
        })
      );
    }

    if (coQuyenDoc("nhan_don")) {
      tasks.push(
        layTongCount("nhan_don", async () => {
          const { data } = await nhanDonService.layDanhSachOrderAPI(1, 1);
          return data?.data?.totalCount ?? 0;
        })
      );
    }

    if (coQuyenDoc("giao_hang")) {
      tasks.push(
        layTongCount("giao_hang", async () => {
          const { data } = await donShipService.layDanhDonShipPhanTrangAPI(1, 1);
          return data?.data?.totalCount ?? 0;
        })
      );
    }

    if (coQuyenDoc("ket_thuc")) {
      tasks.push(
        layTongCount("ket_thuc", async () => {
          const { data } = await ketThucService.layDanhSachDonHangPhanTrangAPI(
            1,
            1,
            "",
            "",
            ""
          );
          return data?.data?.totalCount ?? 0;
        })
      );
    }

    if (coQuyenDoc("chi_phi")) {
      tasks.push(
        layTongCount("chi_phi", async () => {
          const { data } = await chiPhiService.layDanhSachChiPhiAPI(1, 1);
          return data?.data?.totalCount ?? 0;
        })
      );
    }

    if (coQuyenDoc("khach_hang")) {
      tasks.push(
        layTongCount("khach_hang", async () => {
          const { data } = await khachHangService.layDanhSachKhachHangAPI(1, 1, "");
          return data?.data?.danhSachKhachHang?.totalCount ?? 0;
        })
      );
    }

    Promise.all(tasks).finally(() => {
      if (!daHuy) setLoading(false);
    });

    return () => {
      daHuy = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cardsDuocPhep = RESOURCE_CARDS.filter((item) => coQuyenDoc(item.resource));

  return (
    <div>
      <Title level={3} style={{ marginBottom: 4 }}>
        Chào, {userInfo?.tenNhanVien || "bạn"} 👋
      </Title>
      <Text type="secondary">
        Tổng quan nhanh các mảng bạn đang được phân quyền truy cập.
      </Text>

      {cardsDuocPhep.length === 0 ? (
        <Empty
          style={{ marginTop: 48 }}
          description="Tài khoản chưa được cấp quyền truy cập module nào. Liên hệ quản trị viên để được cấp quyền."
        />
      ) : (
        <>
          {coQuyenDoc("don_hang") && (
            <Card
              title={
                <span>
                  <ShoppingCartOutlined /> Đơn Hàng Cần Chú Ý
                </span>
              }
              style={{ marginTop: 24 }}
              hoverable
              onClick={() => history.push("/don-hang")}
            >
              <Row gutter={16}>
                {TRANG_THAI_DON_HANG_CAN_THEO_DOI.map((tt) => {
                  const soLuong = donHangTheoTrangThai.find(
                    (item) => item.id === tt.id
                  )?.soLuong;
                  return (
                    <Col xs={24} sm={8} key={tt.id}>
                      <Statistic
                        title={tt.ten}
                        value={soLuong ?? undefined}
                        loading={loading && soLuong === undefined}
                        valueStyle={
                          tt.id === 9 ? { color: "#f5222d" } : undefined
                        }
                      />
                    </Col>
                  );
                })}
              </Row>
            </Card>
          )}

          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            {cardsDuocPhep
              .filter((item) => item.resource !== "don_hang")
              .map((item) => (
                <Col xs={24} sm={12} md={8} lg={6} key={item.resource}>
                  <Card
                    hoverable
                    onClick={() => history.push(item.path)}
                    style={{ height: "100%" }}
                  >
                    <div
                      style={{
                        fontSize: 22,
                        color: item.color,
                        marginBottom: 8,
                      }}
                    >
                      {item.icon}
                    </div>
                    {item.key ? (
                      <Statistic
                        title={item.label}
                        value={tongQuan[item.key] ?? undefined}
                        loading={loading && tongQuan[item.key] === undefined}
                        suffix={
                          tongQuan[item.key] === null ? (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              (không tải được)
                            </Text>
                          ) : null
                        }
                      />
                    ) : (
                      <>
                        <Text strong>{item.label}</Text>
                        <div>
                          <Text type="secondary">Xem chi tiết →</Text>
                        </div>
                      </>
                    )}
                  </Card>
                </Col>
              ))}
          </Row>
        </>
      )}
    </div>
  );
}
